import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { GET_COMERCIOS } from '../graphql/queries'
import './Comercios.css'

function Comercios() {
  const navigate = useNavigate()
  const [filtros, setFiltros] = useState({
    busqueda: '',
    estado: '',
    tipo_interfaz: ''
  })
  const [page, setPage] = useState(0)
  const limit = 20

  const { data, loading, refetch } = useQuery(GET_COMERCIOS, {
    variables: {
      busqueda: filtros.busqueda || null,
      estado: filtros.estado || null,
      tipo_interfaz: filtros.tipo_interfaz || null,
      limit,
      offset: page * limit
    }
  })

  const comercios = data?.comercios || []
  const total = data?.totalComercios || 0
  const totalPages = Math.ceil(total / limit)

  const handleFiltroChange = (e) => {
    const { name, value } = e.target
    setFiltros(prev => ({ ...prev, [name]: value }))
    setPage(0)
  }

  const handleBusqueda = (e) => {
    e.preventDefault()
    refetch()
  }

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'activo': return <span className="badge badge-success">Activo</span>
      case 'pendiente': return <span className="badge badge-warning">Pendiente</span>
      case 'suspendido': return <span className="badge badge-danger">Suspendido</span>
      default: return <span className="badge badge-gray">{estado}</span>
    }
  }

  const getLicenciaBadge = (licencia) => {
    if (!licencia) {
      return <span className="badge badge-gray">Sin licencia</span>
    }

    if (licencia.dias_restantes <= 0) {
      return <span className="badge badge-danger">Vencida</span>
    }

    if (licencia.dias_restantes <= 7) {
      return <span className="badge badge-danger">{licencia.dias_restantes}d</span>
    }

    if (licencia.dias_restantes <= 30) {
      return <span className="badge badge-warning">{licencia.dias_restantes}d</span>
    }

    return <span className="badge badge-success">{licencia.dias_restantes}d</span>
  }

  return (
    <div className="comercios-page">
      <div className="page-header flex-between">
        <div>
          <h1 className="page-title">Comercios</h1>
          <p className="page-subtitle">{total} comercios registrados</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="card mb-3">
        <div className="card-body">
          <form onSubmit={handleBusqueda} className="filtros-form">
            <div className="filtro-group">
              <input
                type="text"
                name="busqueda"
                value={filtros.busqueda}
                onChange={handleFiltroChange}
                className="form-input"
                placeholder="Buscar por NIT o nombre..."
              />
            </div>

            <div className="filtro-group">
              <select
                name="estado"
                value={filtros.estado}
                onChange={handleFiltroChange}
                className="form-select"
              >
                <option value="">Todos los estados</option>
                <option value="activo">Activo</option>
                <option value="pendiente">Pendiente</option>
                <option value="suspendido">Suspendido</option>
              </select>
            </div>

            <div className="filtro-group">
              <select
                name="tipo_interfaz"
                value={filtros.tipo_interfaz}
                onChange={handleFiltroChange}
                className="form-select"
              >
                <option value="">Todas las interfaces</option>
                <option value="restaurante">Restaurante</option>
                <option value="retail">Retail</option>
                <option value="hotel">Hotel</option>
                <option value="servicios">Servicios</option>
                <option value="pos_rapido">POS Rapido</option>
              </select>
            </div>

            <button type="submit" className="btn btn-primary">
              Buscar
            </button>
          </form>
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : comercios.length === 0 ? (
            <div className="text-center" style={{ padding: '2rem' }}>
              <p className="text-muted">No se encontraron comercios</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>NIT</th>
                  <th>Comercio</th>
                  <th>Ciudad</th>
                  <th>Interfaz</th>
                  <th>Estado</th>
                  <th>Licencia</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {comercios.map((comercio) => (
                  <tr
                    key={comercio.id}
                    onClick={() => navigate(`/comercios/${comercio.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{comercio.nit}</td>
                    <td>
                      <div>
                        <strong>{comercio.nombre_comercial}</strong>
                        <br />
                        <small className="text-muted">{comercio.razon_social}</small>
                      </div>
                    </td>
                    <td>{comercio.ciudad || '-'}</td>
                    <td style={{ textTransform: 'capitalize' }}>{comercio.tipo_interfaz}</td>
                    <td>{getEstadoBadge(comercio.estado)}</td>
                    <td>
                      {comercio.licencia_activa ? (
                        <div>
                          {getLicenciaBadge(comercio.licencia_activa)}
                          <br />
                          <small className="text-muted">
                            {comercio.licencia_activa.tipo_licencia_nombre}
                          </small>
                        </div>
                      ) : (
                        <span className="text-muted">Sin licencia</span>
                      )}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-secondary">
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="card-footer">
            <div className="paginacion">
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setPage(p => p - 1)}
                disabled={page === 0}
              >
                Anterior
              </button>
              <span className="paginacion-info">
                Página {page + 1} de {totalPages}
              </span>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Comercios
